import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface DatasetDownloadParams {
  datasetId: number;
  isTemp: boolean;
}

export const useTempDatasetDownload = (options?: {
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  return useMutation({
    mutationFn: async ({ datasetId, isTemp }: DatasetDownloadParams) => {
      try {
        // Use different endpoint based on isTemp flag
        const endpoint = isTemp
          ? `/sampling-planning/temp-datasets/eno1/${datasetId}`
          : `/data-set/eno1/download?dataSet=${datasetId}`;
        
        const response = await axios.get(endpoint, {
          responseType: 'blob',
        });

        let fileName: string | null = null;
        
        // Try to extract the filename from the Content-Disposition header
        // In some browsers/configurations this might work despite CORS issues
        const contentDisposition = 
          response.headers['content-disposition'] || 
          response.headers['Content-Disposition'];
        
        if (contentDisposition) {
          // Match the filename in Content-Disposition header
          const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
          if (filenameMatch && filenameMatch[2]) {
            fileName = decodeURIComponent(filenameMatch[2]);
          }
        }
        
        // If we couldn't get the filename from the header, generate one (same logic style as NO2)
        if (!fileName) {
          const date = new Date();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const timestamp =
            String(date.getHours()).padStart(2, '0') +
            String(date.getMinutes()).padStart(2, '0') +
            String(date.getSeconds()).padStart(2, '0');

          // Consistent fallback naming without IDs to mirror NO2 pattern
          fileName = `ENO1_TempDataset_${year}${month}${day}_${timestamp}.csv`;
        }

        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'text/csv' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading temp dataset file:', error);
        throw error;
      }
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
};
